import { handleErrors, simplePostRequestWithBody } from "../../API/baseAPI";
import { isUserLoggedIn } from "../../UIController/user";
import { showAlert } from "../../Util/alerts";
import { cleanupCaptcha, getCaptchaResponse, initializeCaptcha, resetCaptcha, sitekey } from "../../Util/captcha";
import { hideModal, showModal } from "../../Util/helper";

export class SuggestionManager {
    constructor() {
        this.currentEditStep = 1;
        this.totalEditSteps = 3;
        this.editStepsCompleted = [false, false, false];
        this.editSuggestion = {
            locationId: null,
            corrections: [],
            suggesterEmail: null,
            description: null
        };
    }

    static getInstance() {
        if (!SuggestionManager.instance) {
            SuggestionManager.instance = new SuggestionManager();
        }
        return SuggestionManager.instance;
    }

    initEditSuggestionSystem() {
        if (isUserLoggedIn()) {
            this.editSuggestion.suggesterEmail = alertUser.email;
            document.getElementById('suggesterEmail').value = alertUser.email;
            document.getElementById('suggesterEmail').disabled = true;
        }
    }

    async showEditSuggestionModal(config = {}) {
        this.resetEditSuggestionForm();
        
        if (config.locationId) {
            this.editSuggestion.locationId = config.locationId;
            document.getElementById('locationName').textContent = config.locationName || config.locationId;
            this.editStepsCompleted[0] = true;
        }
        
        this.updateEditStepIndicators();

        if (this.currentEditStep === 3) {
            await this.initializeSuggestionCaptcha();
        }
        
        showModal('editSuggestionModal');
    }

    updateEditStepIndicators() {
        for (let i = 1; i <= this.totalEditSteps; i++) {
            const indicator = document.getElementById(`editStep${i}Indicator`);
            
            if (i < this.currentEditStep || this.editStepsCompleted[i-1]) {
                // Completed steps
                indicator.classList.add('step-primary');
            } else if (i === this.currentEditStep) {
                // Current step
                indicator.classList.add('step-primary');
            } else {
                // Future steps
                indicator.classList.remove('step-primary');
            }
        }
    }

    async initializeSuggestionCaptcha() {
        await initializeCaptcha({
            containerId: 'editCaptcha',
            submitButtonId: 'editSubmitButton',
            onSuccess: () => {
            },
            onError: () => {
                showAlert('Captcha validation failed. Please try again.', 'error');
            },
            onExpire: () => {
                resetCaptcha('editCaptcha');
            }
        });
    }

    resetEditSuggestionForm() {
        this.editSuggestion = {
            locationId: null,
            corrections: [],
            suggesterEmail: null,
            description: null
        };
        
        this.currentEditStep = 1;
        this.editStepsCompleted = [false, false, false];
        
        // Reset UI elements
        document.getElementById('editDescription').value = '';
        document.querySelectorAll('.correction-input').forEach(input => input.value = '');
        document.querySelectorAll('.correction-checkbox').forEach(checkbox => checkbox.checked = false);
        
        hideAllEditStepContents();
        document.getElementById('editSelectionStep').classList.remove('hidden');
        
        document.getElementById('editSubmitButton').classList.add('hidden');
        document.getElementById('editBack').classList.add('hidden');
        
        if (isUserLoggedIn()) {
            this.editSuggestion.suggesterEmail = alertUser.email;
            document.getElementById('suggesterEmail').value = alertUser.email;
            document.getElementById('suggesterEmail').disabled = true;
        } else {
            document.getElementById('suggesterEmail').disabled = false;
        }
        
        document.getElementById('correctionsSummary').innerHTML = '';
        this.updateEditStepIndicators();
    }

    toggleCorrection(correctionType) {
        const checkbox = document.getElementById(`${correctionType}Checkbox`);
        const inputContainer = document.getElementById(`${correctionType}InputContainer`);
        
        if (checkbox.checked) {
            // Show the input field for this correction
            inputContainer.classList.remove('hidden');
            
            // Add this correction type to our corrections array if not already there
            if (!this.editSuggestion.corrections.some(c => c.type === correctionType)) {
                const currentValue = getCurrentValueForCorrectionType(correctionType);
    
                this.editSuggestion.corrections.push({
                    type: correctionType,
                    currentValue: currentValue,
                    suggestedValue: ''
                });
            }
        } else {
            inputContainer.classList.add('hidden');
            this.editSuggestion.corrections = this.editSuggestion.corrections.filter(c => c.type !== correctionType);
        }
        
        document.getElementById('editNextButton').disabled = this.editSuggestion.corrections.length === 0;
    }

    updateCorrectionValue(correctionType, value) {
        const correction = this.editSuggestion.corrections.find(c => c.type === correctionType);
        
        if (correction) {
            correction.suggestedValue = value;
        }
    }

    confirmCorrectionsSelection() {
        const invalidCorrections = this.editSuggestion.corrections.filter(
            c => c.suggestedValue.trim() === ''
        );
        
        if (invalidCorrections.length > 0) {
            const correctionNames = invalidCorrections.map(c => getCorrectionDisplayName(c.type)).join(', ');
            showAlert(`Please provide values for: ${correctionNames}`, 'warning');
            return;
        }
        
        this.editStepsCompleted[0] = true;
        
        this.goToNextEditStep();
    }

    async goToNextEditStep() {
        if (!this.validateCurrentEditStep()) {
            return;
        }
        
        hideAllEditStepContents();
        this.currentEditStep++;
        
        if (this.currentEditStep === 3) {
            await this.initializeSuggestionCaptcha();
        }
    
        switch(this.currentEditStep) {
            case 2:
                document.getElementById('editDescriptionStep').classList.remove('hidden');
                break;
            case 3:
                document.getElementById('editConfirmStep').classList.remove('hidden');
                this.prepareEditSummary();
                break;
        }
        
        this.updateEditNavigationButtons();
        this.updateEditStepIndicators();
    }

    goToPreviousEditStep() {
        hideAllEditStepContents();
        this.currentEditStep--;

        switch(this.currentEditStep) {
            case 1:
                document.getElementById('editSelectionStep').classList.remove('hidden');
                break;
            case 2:
                document.getElementById('editDescriptionStep').classList.remove('hidden');
                break;
        }
        
        this.updateEditNavigationButtons();
        this.updateEditStepIndicators();
    }

    navigateToEditStep(step) {
        if (step <= this.currentEditStep || this.editStepsCompleted[step-1]) {
            hideAllEditStepContents();
            this.currentEditStep = step;

            switch(step) {
                case 1:
                    document.getElementById('editSelectionStep').classList.remove('hidden');
                    break;
                case 2:
                    document.getElementById('editDescriptionStep').classList.remove('hidden');
                    break;
                case 3:
                    document.getElementById('editConfirmStep').classList.remove('hidden');
                    this.prepareEditSummary();
                    break;
            }
            
            this.updateEditNavigationButtons();
            this.updateEditStepIndicators();
        } else if ((this.currentEditStep + 1) == step) {
            this.goToNextEditStep();
        } else {
            showAlert('Please complete the previous steps first.', 'warning');
        }
    }

    updateEditNavigationButtons() {
        const backButton = document.getElementById('editBack');
        const submitButton = document.getElementById('editSubmitButton');
        
        if (this.currentEditStep > 1) {
            backButton.classList.remove('hidden');
        } else {
            backButton.classList.add('hidden');
        }
        
        if (this.currentEditStep < this.totalEditSteps) {
            submitButton.classList.add('hidden');
        } else {
            submitButton.classList.remove('hidden');
        }
    }

    validateCurrentEditStep() {
        switch (this.currentEditStep) {
            case 1:
                if (this.editSuggestion.corrections.length === 0) {
                    showAlert('Please select at least one correction to suggest.', 'warning');
                    return false;
                }
                
                const invalidCorrections = this.editSuggestion.corrections.filter(
                    c => c.suggestedValue.trim() === ''
                );
                
                if (invalidCorrections.length > 0) {
                    const correctionNames = invalidCorrections.map(c => getCorrectionDisplayName(c.type)).join(', ');
                    showAlert(`Please provide values for: ${correctionNames}`, 'warning');
                    return false;
                }
                
                this.editStepsCompleted[0] = true;
                return true;
                
            case 2: 
                this.editSuggestion.description = document.getElementById('editDescription').value.trim();
                
                if (!this.editSuggestion.description) {
                    showAlert('Please provide a brief explanation for your suggested changes.', 'warning');
                    return false;
                }
                
                this.editStepsCompleted[1] = true;
                return true;
                
            default:
                return true;
        }
    }

    prepareEditSummary() {
        const summaryContainer = document.getElementById('correctionsSummary');
        summaryContainer.innerHTML = '';
        
        this.editSuggestion.corrections.forEach(correction => {
            const correctionItem = document.createElement('div');
            correctionItem.className = 'mb-2';
            
            const correctionTitle = document.createElement('h4');
            correctionTitle.className = 'font-bold';
            correctionTitle.textContent = getCorrectionDisplayName(correction.type);
            
            const correctionValue = document.createElement('p');
            correctionValue.textContent = `Suggested value: ${correction.suggestedValue}`;
            
            correctionItem.appendChild(correctionTitle);
            correctionItem.appendChild(correctionValue);
            summaryContainer.appendChild(correctionItem);
        });
        
        document.getElementById('summaryEditDescription').textContent = this.editSuggestion.description;
        document.getElementById('summaryEditEmail').textContent = this.editSuggestion.suggesterEmail || 'Not provided';
    }
    
    cancelEditSuggestion() {
        cleanupCaptcha('editCaptcha');
        hideModal('editSuggestionModal');
        this.resetEditSuggestionForm();
    }

    submitEditSuggestion() {
        const submitButton = document.getElementById('editSubmitButton');
        submitButton.disabled = true;
        
        const editData = {
            id: this.editSuggestion.locationId,
            corrections: this.editSuggestion.corrections,
            description: this.editSuggestion.description,
            suggesterEmail: this.editSuggestion.suggesterEmail,
            captchaResponse: getCaptchaResponse('editCaptcha'),
            sitekey: sitekey
        };
        
        simplePostRequestWithBody('/api/v1/edit', editData, 'editSubmitButton', (response) => {
            resetCaptcha('editCaptcha');
            submitButton.disabled = false;
            
            if (response.error) {
                handleErrors(response);
                return;
            }
            
            hideModal('editSuggestionModal');
            showAlert(response.message || 'Your edit suggestions were submitted successfully.', 'success');
            hideModal('editSuggestionModal');
            this.resetEditSuggestionForm();
        });
    }
}

function getCurrentValueForCorrectionType(correctionType) {
    switch(correctionType) {
        case 'price':
            return 'Current price range';
        case 'hours':
            return 'Current opening hours';
        case 'address':
            return 'Current address';
        case 'seats':
            return 'Current available seats';
        case 'mainIngredient':
            return 'Current main ingredients';
        case 'reviewFacts':
            return 'Current facts mentioned in reviews';
        case 'amenities':
            return 'Current amenities';
        case 'other':
            return 'Current other information';
        default:
            return '';
    }
}

// Get display name for a correction type
function getCorrectionDisplayName(correctionType) {
    const correctionMap = {
        'price': 'Price Range',
        'hours': 'Opening Hours',
        'address': 'Address',
        'seats': 'Available Seats',
        'mainIngredient': 'Main Ingredient',
        'reviewFacts': 'Facts Mentioned in Reviews',
        'amenities': 'Amenities',
        'other': 'Other Information'
    };
    
    return correctionMap[correctionType] || correctionType;
}

// Hide all step content divs
function hideAllEditStepContents() {
    document.querySelectorAll('.edit-step-content').forEach(el => {
        el.classList.add('hidden');
    });
}